import { Navigate, type RouteObject, useLocation } from 'react-router-dom';

import { AppLayout } from './components/AppLayout';
import { isAuthenticated } from './lib/auth';
import { AiSettingsPage } from './pages/AiSettings';
import { DashboardPage } from './pages/Dashboard';
import { DeviceDashboardPage } from './pages/DeviceDashboard';
import { DisplayConfigFilesPage } from './pages/DisplayConfigFiles';
import { DisplayGlobalMediaPage } from './pages/DisplayGlobalMedia';
import { DisplayPublishPage } from './pages/DisplayPublish';
import { DisplayTelemetryPage } from './pages/DisplayTelemetry';
import { DisplayUpdateSimulationPage } from './pages/DisplayUpdateSimulation';
import { ForcedReclaimPage } from './pages/ForcedReclaim';
import { LoginPage } from './pages/Login';
import { ManualReviewDetailPage } from './pages/ManualReviewDetail';
import { ManualReviewListPage } from './pages/ManualReviewList';
import { MediaLibraryPage } from './pages/MediaLibrary';
import { NotificationDispatchDetailPage } from './pages/NotificationDispatchDetail';
import { NotificationsDlqListPage } from './pages/NotificationsDlqList';
import { OrderDetailPage } from './pages/OrderDetail';
import { OrdersListPage } from './pages/OrdersList';
import { PartnerDetailPage } from './pages/PartnerDetail';
import { PartnersListPage } from './pages/PartnersList';
import { PermissionsCatalogPage } from './pages/PermissionsCatalog';
import { PricingEditPage } from './pages/PricingEdit';
import { PricingListPage } from './pages/PricingList';
import { ProductEditPage } from './pages/ProductEdit';
import { ProductsListPage } from './pages/ProductsList';
import { QuarantineBoardPage } from './pages/QuarantineBoard';
import { RecommendationWeightsPage } from './pages/RecommendationWeights';
import { RefundPoliciesListPage } from './pages/RefundPoliciesList';
import { RefundPolicyEditPage } from './pages/RefundPolicyEdit';
import { RefundsListPage } from './pages/RefundsList';
import { ReleaseManifestBoardPage } from './pages/ReleaseManifestBoard';
import { ReportsPage } from './pages/Reports';
import { RoleDetailPage } from './pages/RoleDetail';
import { RolesListPage } from './pages/RolesList';
import { ScheduleEditPage } from './pages/ScheduleEdit';
import { SchedulePlanPage } from './pages/SchedulePlanPage';
import { SchedulesListPage } from './pages/SchedulesList';
import { SpotEditPage } from './pages/SpotEdit';
import { SpotsListPage } from './pages/SpotsList';
import { StreamsDlqListPage } from './pages/StreamsDlqList';
import { UserDetailPage } from './pages/UserDetail';
import { UsersListPage } from './pages/UsersList';

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'spots', element: <SpotsListPage /> },
      { path: 'spots/new', element: <SpotEditPage /> },
      { path: 'spots/:id', element: <SpotEditPage /> },
      { path: 'media', element: <MediaLibraryPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'refunds', element: <RefundsListPage /> },
      { path: 'refunds/manual-reviews', element: <ManualReviewListPage /> },
      { path: 'refunds/manual-reviews/:id', element: <ManualReviewDetailPage /> },
      // A-1 商品管理
      { path: 'products', element: <ProductsListPage /> },
      { path: 'products/new', element: <ProductEditPage /> },
      { path: 'products/:id', element: <ProductEditPage /> },
      // A-2 班次管理（new = 重設計之排班頁；:id = 編輯既有班次容量/天候停航）
      { path: 'schedules', element: <SchedulesListPage /> },
      { path: 'schedules/new', element: <SchedulePlanPage /> },
      { path: 'schedules/:id', element: <ScheduleEditPage /> },
      // A-3 定價推廣
      { path: 'pricing', element: <PricingListPage /> },
      { path: 'recommendation-weights', element: <RecommendationWeightsPage /> },
      { path: 'pricing/new', element: <PricingEditPage /> },
      { path: 'pricing/:id', element: <PricingEditPage /> },
      // B-2 退票政策（AC-B2-1 launch-blocker）
      { path: 'refund-policies', element: <RefundPoliciesListPage /> },
      { path: 'refund-policies/new', element: <RefundPolicyEditPage /> },
      { path: 'refund-policies/:id', element: <RefundPolicyEditPage /> },
      // H-6 / H-8 通知中心（DLQ 列表 + dispatch 詳情）
      { path: 'notifications/dlq', element: <NotificationsDlqListPage /> },
      { path: 'notifications/:id', element: <NotificationDispatchDetailPage /> },
      // D-2 離線檢票（裝置隔離看板 + 強制收回）
      { path: 'device-quarantines', element: <QuarantineBoardPage /> },
      { path: 'leases/forced-reclaim', element: <ForcedReclaimPage /> },
      // F-2 Release Manifest 看板
      { path: 'release-manifests', element: <ReleaseManifestBoardPage /> },
      // 船機設定檔（config/ 三個檔的原始文字）
      // 窗屏內容發布（打包已發布景點成一版）
      { path: 'display-publish', element: <DisplayPublishPage /> },
      { path: 'display-config-files', element: <DisplayConfigFilesPage /> },
      { path: 'display-global-media', element: <DisplayGlobalMediaPage /> },
      { path: 'display-update-simulation', element: <DisplayUpdateSimulationPage /> },
      { path: 'display-telemetry', element: <DisplayTelemetryPage /> },
      // B-1 訂單管理（drill-down 自 Dashboard KPI）
      { path: 'admin/orders', element: <OrdersListPage /> },
      { path: 'admin/orders/:id', element: <OrderDetailPage /> },
      // H-3 設備儀表板
      { path: 'admin/devices', element: <DeviceDashboardPage /> },
      // I-6 戰情同步異常清單（streams DLQ）
      { path: 'streams/dlq', element: <StreamsDlqListPage /> },
      // 系統管理 — 權限管理（切片 1 唯讀 + 切片 2/3 編輯）
      { path: 'admin/users', element: <UsersListPage /> },
      { path: 'admin/users/:id', element: <UserDetailPage /> },
      { path: 'admin/roles', element: <RolesListPage /> },
      { path: 'admin/roles/:id', element: <RoleDetailPage /> },
      { path: 'admin/permissions', element: <PermissionsCatalogPage /> },
      { path: 'admin/ai-settings', element: <AiSettingsPage /> },
      // G 夥伴 API 管理
      { path: 'admin/partners', element: <PartnersListPage /> },
      { path: 'admin/partners/:id', element: <PartnerDetailPage /> },
    ],
  },
  { path: '/login', element: <LoginPage /> },
  { path: '*', element: <Navigate to="/" replace /> },
];
